import { useEffect, useRef } from 'react';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { trackConversionEvent } from '@/lib/adsTracking';
import { ADS_ANALYTIC_EVENTS } from '@/lib/constants';
import { configSettings } from '@/lib/config';

interface SignupSuccessData {
    userId: number | string;
    username: string;
    email: string;
    fullName: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
}

/**
 * Hook for tracking registration analytics events.
 * Matches Vue's Register.vue and RegisterForm.vue analytics implementation.
 *
 * Events tracked:
 * - On mount: AddToCart (ad pixels), pixel-AddToCart, signup-form-display
 * - On success: signup, CompleteRegistration (ad pixels), Northbeam events
 */
export function useRegistrationAnalytics() {
    const hasTrackedFormDisplay = useRef(false);

    /**
     * Track form display events - called once when the registration form mounts.
     * Matches Vue's Register.vue mounted() analytics.
     */
    useEffect(() => {
        if (hasTrackedFormDisplay.current) return;
        hasTrackedFormDisplay.current = true;

        // Track AddToCart event on ad pixels (Facebook, TikTok, LinkedIn)
        // Matches: this.$AdsTracking('track', CONSTANTS.ADS_ANALYTIC_EVENTS.ADD_TO_CART, {...})
        trackConversionEvent(ADS_ANALYTIC_EVENTS.ADD_TO_CART, {
            content_name: 'Start Signup',
            content_category: 'Signup Process',
        });

        // Track pixel-AddToCart event
        // Matches: this.$stanAnalytics('pixel-AddToCart', { meta: { username: 'guest' } })
        stanAnalytics('pixel-AddToCart', {
            meta: { username: 'guest' },
        });

        // Track signup form display event
        // Matches: this.$stanAnalytics('signup-form-display', { meta: { username: 'guest' } })
        stanAnalytics('signup-form-display', {
            meta: { username: 'guest' },
        });
    }, []);

    /**
     * Track successful registration.
     * Matches Vue's RegisterForm.vue success analytics.
     */
    const trackSignupSuccess = (data: SignupSuccessData) => {
        const { userId, username, email, fullName, utmSource, utmMedium, utmCampaign } = data;

        // Build props object with UTM data if available
        const props: Record<string, string> = {
            email,
            fullName,
        };

        if (utmSource) props.utmSource = utmSource;
        if (utmMedium) props.utmMedium = utmMedium;
        if (utmCampaign) props.utmCampaign = utmCampaign;

        // Track signup event
        // Matches: this.$stanAnalytics('signup', { meta: { user_id, username }, props: {...} })
        stanAnalytics('signup', {
            meta: {
                user_id: userId,
                username,
            },
            props: {
                ...props,
                experiments: {}, // Placeholder for experiments
            },
        });

        // Track CompleteRegistration event on ad pixels
        // Matches: this.$AdsTracking('track', CONSTANTS.ADS_ANALYTIC_EVENTS.COMPLETE_REGISTRATION, {...}, { user_id })
        trackConversionEvent(
            ADS_ANALYTIC_EVENTS.COMPLETE_REGISTRATION,
            {
                content_name: 'Signup Completion',
                content_category: 'Signup Process',
            },
            { user_id: userId }
        );

        // Northbeam tracking (production only)
        // Matches: window.Northbeam.fireEmailCaptureEvent(response.email, 'stan_app')
        // Matches: window.Northbeam.fireCustomGoal('email_signup', {})
        if (configSettings.environment === 'production' && window.Northbeam) {
            try {
                window.Northbeam.fireEmailCaptureEvent(email, 'stan_app');
                window.Northbeam.fireCustomGoal('email_signup', {});
            } catch (error) {
                console.error('Northbeam tracking error:', error);
            }
        }
    };

    /**
     * Track registration failure.
     * This is a React-specific addition for better error tracking.
     */
    const trackSignupFailure = (errorType: string, errorMessage?: string) => {
        stanAnalytics('signup-failure', {
            meta: { username: 'guest' },
            props: {
                errorType,
                errorMessage: errorMessage || 'Unknown error',
            },
        });
    };

    return {
        trackSignupSuccess,
        trackSignupFailure,
    };
}
